import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';

import styles from 'components/shared.module.scss';
import { EmptyUserAvatar } from 'components/UserAvatar';
import { apiErrorHandler } from 'core/errors';
import { Dispatch } from 'redux';
import { UPDATE_COURSE_ADMIN, UPDATE_COURSE_DETAILS } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { getRawUser } from 'utils';

import AcadlyLogo from 'assets/acadly-icon.png';
import { ReactComponent as CrossIcon } from 'assets/cross.svg';
import ErrorIcon from 'assets/error.png';

import UserAvatar from '../UserAvatar';
import { Button, LoaderSpinner, TextField } from '../UtilElements';

interface SaveCourseInfoPayload {
  courseId: string;
  code: string; // Max 16 characters
  title: string;
  description: string;
  team: {
    isAdminSet: NumericBoolean;
    admin: RawUser;
    instructors: RawUser[];
    tas: RawUser[];
  };
}

const mapState = (state: RootState) => ({
  course: state.courseInCreation,
});

const mapDispatch = (dispatch: Dispatch) => ({
  updateAdmin: async (userId: string) => {
    dispatch({
      type: UPDATE_COURSE_ADMIN,
      payload: userId,
    });
  },
  updateCourse: async (course: CourseDetails) => {
    dispatch({
      type: UPDATE_COURSE_DETAILS,
      payload: course,
    });
  },
});

interface OwnProps {
  onNext(): void;
}

type Props = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch> & ToastManagerProp & OwnProps;

interface State {
  isLoading: boolean;
  code: string;
  codeError: string;
  title: string;
  titleError: string;
}

class CourseInfo extends Component<Props, State> {
  state: State = {
    isLoading: false,
    code: this.props.course?.code || '',
    codeError: '',
    title: this.props.course?.title || '',
    titleError: '',
  };

  componentDidUpdate(prevProps: Props) {
    if (this.props.course?.courseId !== prevProps.course?.courseId) {
      this.setState({
        code: this.props.course?.code || '',
        codeError: '',
        title: this.props.course?.title || '',
        titleError: '',
      });
    }
  }

  saveCourseInfo = async () => {
    const course = this.props.course;
    const { code, title } = this.state;

    if (!course) return;

    if (!code || code.length > 16 || !title) {
      return this.setState({
        codeError: !code
          ? 'Please provide a course code'
          : code.length > 16
          ? 'Course code can not be more than 16 characters'
          : '',
        titleError: !title ? 'Please provide a course title' : '',
      });
    }

    const data: SaveCourseInfoPayload = {
      code,
      title,
      courseId: course.courseId,
      description: course.description,
      team: {
        isAdminSet: 1,
        admin: getRawUser(course.team.admin as CourseUser),
        instructors: [...course.team.instructors, course.team.admin as CourseUser]
          .filter((u) => !!u.valid)
          .map(getRawUser),
        tas: course.team.tas.filter((u) => !!u.valid).map(getRawUser),
      },
    };

    try {
      this.setState({ isLoading: true });
      await request({ url: API.COURSE_CREATE_SAVE_INFO, method: 'patch', data });
      this.setState({ isLoading: false });

      this.props.updateCourse({ ...course, code, title });
      this.props.onNext();
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ isLoading: false });
      });
    }
  };

  render() {
    const { course } = this.props;
    const { isLoading, code, codeError, title, titleError } = this.state;

    return (
      <>
        <form className='import-course__form'>
          <label className='import-course__label'>Course Code</label>
          {course ? (
            <TextField
              type='text'
              wrapperClassName='import-course__gutters'
              placeholder='Course code'
              value={code}
              onchange={(e) =>
                this.setState({
                  code: e.target.value,
                  codeError: '',
                })
              }
            />
          ) : (
            <div className='loader-strip' />
          )}
          {codeError && (
            <div className='import-course__error'>
              <img src={ErrorIcon} alt='' />
              <span>{codeError}</span>
            </div>
          )}
          <label className='import-course__label'>Course Title</label>
          {course ? (
            <TextField
              type='text'
              wrapperClassName='import-course__gutters'
              placeholder='Course title'
              value={title}
              onchange={(e) =>
                this.setState({
                  title: e.target.value,
                  titleError: '',
                })
              }
            />
          ) : (
            <div className='loader-strip' />
          )}
          {titleError && (
            <div className='import-course__error'>
              <img src={ErrorIcon} alt='' />
              <span>{titleError}</span>
            </div>
          )}
          <label className='import-course__label'>Description</label>
          {course ? (
            <p className='import-course__gutters'>{course.description}</p>
          ) : (
            <div className='loader-strip' />
          )}
          <label className='import-course__label'>Course Team</label>
          <div className='import-course__subtitle'>Course Admin</div>
          {course ? (
            course.team.isAdminSet ? (
              <UserAvatar className='import-course__gutters' {...course.team.admin} hasActions>
                {course.team.admin.hasAcadlyAccount === 1 ? (
                  <div className='has-tooltip'>
                    <div className='tooltip-trigger left' data-tooltip='Has Acadly account'>
                      <img
                        src={AcadlyLogo}
                        className={styles['acadly-inline-icon']}
                        alt='Has Acadly account'
                      />
                    </div>
                  </div>
                ) : null}
                <div className='remove-action has-tooltip' onClick={() => this.props.updateAdmin('')}>
                  <div className={'tooltip-trigger left'} data-tooltip='Remove admin'>
                    <CrossIcon className={styles['cross-icon']} />
                  </div>
                </div>
              </UserAvatar>
            ) : (
              <EmptyUserAvatar
                className='import-course__gutters'
                title='No Administrator selected'
                message='Please select an Acadly Course Administrator from the list of instructors below'
              />
            )
          ) : (
            <span className='loader-strip' />
          )}
          <div className='import-course__subtitle'>Instructors</div>
          {course ? (
            course.team.instructors.length ? (
              course.team.instructors.map((instructor) => (
                <Fragment key={instructor.userId}>
                  <UserAvatar className='import-course__gutters' {...instructor} hasActions>
                    {instructor.hasAcadlyAccount === 1 ? (
                      <div className='has-tooltip'>
                        <div className='tooltip-trigger left' data-tooltip='Has Acadly account'>
                          <img
                            src={AcadlyLogo}
                            className={styles['acadly-inline-icon']}
                            alt='Has Acadly account'
                          />
                        </div>
                      </div>
                    ) : null}
                    {instructor.userId && instructor.valid ? (
                      <Button
                        type='ghost'
                        disabled={!!course.team.isAdminSet}
                        selector={`btn-small ${styles['make-admin']}`}
                        label='Make Admin'
                        onRipple={() => this.props.updateAdmin(instructor.userId)}
                      />
                    ) : null}
                  </UserAvatar>
                  {!instructor.valid && (
                    <div className='import-course__error'>
                      <img src={ErrorIcon} alt='' />
                      <span>{instructor.invalidReason}</span>
                    </div>
                  )}
                </Fragment>
              ))
            ) : (
              <EmptyUserAvatar
                className='import-course__gutters'
                title='No instructors'
                message='Nobody to show here'
              />
            )
          ) : (
            <span className='loader-strip' />
          )}
          <div className='import-course__subtitle'>Teaching Assistants</div>
          {course ? (
            course.team.tas.length ? (
              course.team.tas.map((ta) => (
                <Fragment key={ta.userId}>
                  <UserAvatar className='import-course__gutters' {...ta} hasActions>
                    {ta.hasAcadlyAccount === 1 ? (
                      <div className='has-tooltip'>
                        <div className='tooltip-trigger left' data-tooltip='Has Acadly account'>
                          <img
                            src={AcadlyLogo}
                            className={styles['acadly-inline-icon']}
                            alt='Has Acadly account'
                          />
                        </div>
                      </div>
                    ) : null}
                  </UserAvatar>
                  {!ta.valid && (
                    <div className='import-course__error'>
                      <img src={ErrorIcon} alt='' />
                      <span>{ta.invalidReason}</span>
                    </div>
                  )}
                </Fragment>
              ))
            ) : (
              <EmptyUserAvatar
                className='import-course__gutters'
                title='No teaching assistants'
                message='Nobody to show here'
              />
            )
          ) : (
            <span className='loader-strip' />
          )}
        </form>
        <section className='import-course__info'>
          <h1 className='import-course__title'>Step 1: Course Info</h1>
          <p className='import-course__description'>
            Course info includes defining a basic course description and confirming the course team. Once the
            course is imported into Acadly, you will be able to upload other course related files and links
            using the Acadly web or mobile apps
          </p>
          <div className='import-course__actions'>
            <Button
              type='colored'
              disabled={isLoading}
              selector={`btn-save-continue btn-primary btn-large ${
                course?.team.isAdminSet ? '' : 'btn-disabled'
              }`}
              onRipple={this.saveCourseInfo}
            >
              {isLoading ? <LoaderSpinner /> : 'Save and continue'}
            </Button>
            <p className='note note__error'>
              {!course?.team.isAdminSet
                ? 'You need to assign an admin for this course'
                : codeError || titleError || ''}
            </p>
          </div>
        </section>
      </>
    );
  }
}

export default connect(mapState, mapDispatch)(withToastManager(CourseInfo));
